/**
 * Bränsletyp-hjälpare (delad logik webb/backend-liknande).
 */

/**
 * Visningsetikett för bränsle. Lagrade Car.info-värden (engine_type) visas ordagrant,
 * t.ex. "Laddhybrid Bensin", "Naturgas (CNG)", "El". Äldre engelska enum-värden
 * (PETROL/DIESEL/ELECTRIC/HYBRID) normaliseras till svenska. Okänt returneras oförändrat.
 */
export function formatFuelTypeLabel(fuelType: string | null | undefined): string {
  if (!fuelType) return '';
  const ft = fuelType.trim();
  if (!ft) return '';
  const legacy: Record<string, string> = {
    petrol: 'Bensin',
    gasoline: 'Bensin',
    diesel: 'Diesel',
    electric: 'El',
    hybrid: 'Hybrid',
    gas: 'Gas',
    ethanol: 'Etanol',
    e85: 'Etanol',
  };
  return legacy[ft.toLowerCase()] ?? ft;
}

export function isElectricFuelType(fuelType: string | null | undefined): boolean {
  if (!fuelType) return false;
  const ft = fuelType.toLowerCase().trim();
  if (!ft) return false;
  // Hybrider och laddhybrider räknas INTE som ren el
  if (ft.includes('hybrid') || ft.includes('phev')) return false;
  // Fossila bränslen (diesel innehåller "el", måste exkluderas före el-kontroll)
  if (
    ft.includes('diesel') ||
    ft.includes('bensin') ||
    ft.includes('petrol') ||
    ft.includes('gasoline') ||
    ft.includes('etanol') ||
    ft.includes('ethanol') ||
    ft.includes('e85') ||
    ft.includes('gas')
  ) {
    return false;
  }
  if (ft === 'bev' || ft === 'ev') return true;
  if (ft.includes('electric') || ft.includes('battery')) return true;
  // Svenska/varierande: "el", "eldrift", "elbil", "elfordon", "eldriven"
  if (ft.includes('el')) return true;
  return false;
}

export type FuelKind = 'electric' | 'petrol' | 'diesel' | 'gas' | 'ethanol';
export type FuelCategory = 'electric' | 'hybrid' | 'fossil';

/** Hybrid / PHEV (inte ren BEV). */
export function isHybridFuelType(fuelType: string | null | undefined): boolean {
  if (!fuelType) return false;
  const ft = fuelType.toLowerCase().trim();
  if (!ft) return false;
  return ft.includes('hybrid') || ft.includes('phev') || ft.includes('plug-in') || ft.includes('plugin');
}

export function getFuelCategory(fuelType: string | null | undefined): FuelCategory {
  if (isElectricFuelType(fuelType)) return 'electric';
  if (isHybridFuelType(fuelType)) return 'hybrid';
  return 'fossil';
}

/**
 * Vilka bränsleslag ett fordon använder. Elbil → El.
 * Hybrid/PHEV → fossilt slag + El (t.ex. Bensin + El).
 */
export function resolveFuelKinds(fuelType: string | null | undefined): FuelKind[] {
  if (!fuelType?.trim()) return [];
  if (isElectricFuelType(fuelType)) return ['electric'];

  const ft = fuelType.toLowerCase().trim();
  const kinds: FuelKind[] = [];

  if (ft.includes('diesel')) {
    kinds.push('diesel');
  } else if (ft.includes('etanol') || ft.includes('ethanol') || ft.includes('e85')) {
    kinds.push('ethanol');
  } else if (
    ft.includes('naturgas') ||
    ft.includes('fordonsgas') ||
    ft.includes('cng') ||
    ft.includes('lpg') ||
    (ft.includes('gas') && !ft.includes('gasoline'))
  ) {
    kinds.push('gas');
  } else if (
    ft.includes('bensin') ||
    ft.includes('petrol') ||
    ft.includes('gasoline') ||
    isHybridFuelType(fuelType)
  ) {
    kinds.push('petrol');
  }

  if (isHybridFuelType(fuelType) && !kinds.includes('electric')) {
    kinds.push('electric');
  }

  return kinds;
}

export function formatFuelKindsLabel(
  fuelType: string | null | undefined,
  labels: Record<FuelKind, string>
): string {
  const kinds = resolveFuelKinds(fuelType);
  if (kinds.length === 0) return formatFuelTypeLabel(fuelType);
  return kinds.map((k) => labels[k]).join(' + ');
}

/** Elbil eller laddhybrid (vägguttag). Självladdande hybrider → false. */
export function isExternallyChargeableFuelType(fuelType: string | null | undefined): boolean {
  if (!fuelType) return false;
  const s = fuelType.toLowerCase().trim();
  if (!s) return false;
  if (s.includes('laddhybrid') || s.includes('plug-in') || s.includes('plugin') || s.includes('phev')) {
    return true;
  }
  if (s.includes('hybrid')) return false;
  return isElectricFuelType(fuelType);
}

/** ecoMetrics.estimatedFuelWaste lagras i ml för fossil; för el (coasting) som Wh×1000. */
export function formatEcoFuelWasteMl(valueMl: number, isElectric: boolean): string {
  if (isElectric) {
    if (valueMl === 0) return '—';
    const kwh = Math.abs(valueMl) / 1000;
    const prefix = valueMl < 0 ? '-' : '';
    return `${prefix}${kwh.toFixed(2)} kWh`;
  }
  if (valueMl < 0) {
    return `-${Math.abs(Math.round(valueMl))} ml`;
  }
  return `${Math.round(valueMl)} ml`;
}

export function formatEcoFuelWasteSummary(valueMl: number, isElectric: boolean): string {
  if (isElectric) {
    if (valueMl === 0) return '—';
    return `${(Math.abs(valueMl) / 1000).toFixed(2)} kWh`;
  }
  return `${(valueMl / 1000).toFixed(2)} L`;
}
